/**
 * A change request result arrives as one entry per row, each with its original, its feedback and the
 * entities its apply changed on the side as affected rows. The CrudTable reads rows, originals and
 * feedback as three collections keyed by row number, so this splits the entries back into them and
 * hangs each affected row under its row as a tree child.
 */

export type ChangeResultRowFeedback = {
  errors: Array<string>;
  cellsFeedback: Record<string, { errors: Array<string>; warnings: Array<string>; suggestions: Array<string>; modified: boolean }>;
};

export type ChangeResultRow<Row> = {
  data: Row;
  original?: Row | null;
  feedback: ChangeResultRowFeedback;
  affectedRows: Array<ChangeResultRow<Row>>;
};

export type SplitChangeResult<Row> = {
  data: Array<Row & { children?: Array<Row> }>;
  originalData: Array<Row>;
  validationFeedback: Record<number, ChangeResultRowFeedback>;
};

/**
 * `flatten` reshapes rows for the table (additional fields, nested values) and is applied to children
 * and originals alike. A child is not a row of the request and carries its parent's row number, so it
 * gets a negative one of its own: unique, never a request row's, and the key its original and its
 * feedback are filed under.
 */
export const splitChangeResult = <Row extends { rowNumber: number }, CrudRow extends { rowNumber: number }>(
  rows: Array<ChangeResultRow<Row>>,
  flatten: (rows: Array<Row>) => Array<CrudRow>,
): SplitChangeResult<CrudRow> => {
  const flattenOne = (row: Row): CrudRow => flatten([row])[0];
  const originalData: Array<CrudRow> = [];
  const validationFeedback: Record<number, ChangeResultRowFeedback> = {};
  let nextChildRowNumber = -1;

  const data = rows.map(({ data: rowData, original, feedback, affectedRows }) => {
    validationFeedback[rowData.rowNumber] = feedback;
    if (original) originalData.push(flattenOne(original));

    const childRows = affectedRows.map(child => {
      const rowNumber = nextChildRowNumber--;
      validationFeedback[rowNumber] = child.feedback;
      if (child.original) originalData.push(flattenOne({ ...child.original, rowNumber }));
      return flattenOne({ ...child.data, rowNumber });
    });

    const row = flattenOne(rowData);
    return childRows.length ? { ...row, children: childRows } : row;
  });

  return { data, originalData, validationFeedback };
};
